import { ChevronDown, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Badge } from '@/components/shadcn/ui/badge';
import { Checkbox } from '@/components/shadcn/ui/checkbox';
import { Input } from '@/components/shadcn/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shadcn/ui/popover';
import { ScrollArea } from '@/components/shadcn/ui/scroll-area';
import useProductStore from '@/features/Product/store/ProductStore';
import { Button } from '@/shared/ui/button';

type ProductFilterFieldProps = {
  value: FilterKeyValue[];
  onChange: (value: FilterKeyValue[]) => void;
};

export const ProductFilterField = ({
  value,
  onChange,
}: ProductFilterFieldProps) => {
  const productMap = useProductStore((s) => s.productMap);
  const brandMap = useProductStore((s) => s.brandMap);
  const lineMap = useProductStore((s) => s.lineMap);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);

  // productMap → 선택 가능한 리스트로 변환 (brand/line 정보 포함)
  const productList = useMemo(() => {
    const items: {
      id: string;
      name: string;
      brandName?: string;
      lineName?: string;
    }[] = [];
    for (const [productId, product] of productMap.entries()) {
      items.push({
        id: productId,
        name: product.product_name ?? productId,
        brandName: product.brand_id
          ? brandMap.get(product.brand_id)?.name
          : undefined,
        lineName: product.line_id
          ? lineMap.get(product.line_id)?.name
          : undefined,
      });
    }
    return items.sort((a, b) => a.name.localeCompare(b.name));
  }, [productMap, brandMap, lineMap]);

  // 검색 필터링 (brand/line도 매칭 대상)
  const filteredList = useMemo(() => {
    if (!searchTerm) return productList;
    const term = searchTerm.toLowerCase();
    return productList.filter(
      (item) =>
        item.name.toLowerCase().includes(term) ||
        item.brandName?.toLowerCase().includes(term) ||
        item.lineName?.toLowerCase().includes(term),
    );
  }, [productList, searchTerm]);

  const selectedKeys = useMemo(
    () => new Set(value.map((v) => v.filterKey)),
    [value],
  );

  const handleToggle = (item: { id: string; name: string }) => {
    if (selectedKeys.has(item.id)) {
      onChange(value.filter((v) => v.filterKey !== item.id));
    } else {
      onChange([...value, { filterKey: item.id, filterName: item.name }]);
    }
  };

  const handleRemove = (filterKey: StringNumberValue) => {
    onChange(value.filter((v) => v.filterKey !== filterKey));
  };

  const handleSelectAll = () => {
    const allValues: FilterKeyValue[] = filteredList.map((item) => ({
      filterKey: item.id,
      filterName: item.name,
    }));
    onChange(allValues);
  };

  const handleDeselectAll = () => {
    onChange([]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex min-h-9 w-full items-center gap-1 rounded-md border border-input bg-background px-2.5 py-1.5 text-left text-xs transition-colors hover:bg-muted/50"
        >
          {value.length === 0 ? (
            <span className="text-muted-foreground">Select products...</span>
          ) : (
            <div className="flex flex-1 flex-wrap gap-1 max-h-[120px] overflow-y-auto">
              {value.map((item) => (
                <Badge
                  key={String(item.filterKey)}
                  variant="secondary"
                  className="flex items-center gap-1"
                >
                  <span className="max-w-[120px] truncate">
                    {item.filterName}
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(item.filterKey);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        handleRemove(item.filterKey);
                      }
                    }}
                    className="rounded-sm transition-colors hover:bg-gray-200"
                    aria-label={`Remove ${item.filterName}`}
                  >
                    <X className="size-3" />
                  </span>
                </Badge>
              ))}
            </div>
          )}
          <ChevronDown className="ml-auto size-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-full p-0 max-w-[600px]" align="start">
        <div className="flex flex-col gap-1.5 p-2">
          {/* 검색 + Select all/Clear */}
          <div className="flex items-center justify-between">
            <div className="relative flex-1">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-8 pl-8 text-xs"
              />
            </div>
            <div className="flex shrink-0 gap-2 pl-2">
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={handleSelectAll}
                className="h-auto p-0 text-xs text-primary"
              >
                Select all
              </Button>
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={handleDeselectAll}
                className="h-auto p-0 text-xs text-muted-foreground"
              >
                Clear
              </Button>
            </div>
          </div>

          {/* 제품 리스트 */}
          <ScrollArea className="max-h-48">
            {filteredList.length === 0 ? (
              <p className="p-3 text-center text-xs text-muted-foreground">
                No products found
              </p>
            ) : (
              <div className="flex flex-col">
                {filteredList.map((item) => {
                  const isSelected = selectedKeys.has(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleToggle(item)}
                      className={`flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted/50 ${
                        isSelected ? 'bg-primary/5' : ''
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        className="pointer-events-none"
                      />
                      <div className="flex flex-wrap gap-1 ">
                        {item.name}
                        {(item.brandName || item.lineName) && (
                          <span className="shrink-0 text-muted-foreground">
                            {[item.brandName, item.lineName]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      </PopoverContent>
    </Popover>
  );
};
